import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Users, Vote, PenLine, FileCheck, Rocket } from "lucide-react";
import { article36Api } from "../../services/article36Api";
import { employeeApi } from "../../services/employeeApi";
import { StatusBadge } from "../../components/StatusBadge";
import { useLocalization } from "../../hooks/useLocalization";
import { formatApiErrorDetail } from "../../lib/api";
import { formatDate } from "../../lib/date";
import { VOTING_METHODS } from "../../lib/constants";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { AccessDenied } from "../../components/AccessDenied";
import { NotFoundState } from "../../components/NotFoundState";

export default function Article36AgreementDetailPage() {
  const { agreementId } = useParams();
  const { t } = useLocalization();
  const [agreement, setAgreement] = useState(null);
  const [election, setElection] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [eligible, setEligible] = useState([]);
  const [candidate, setCandidate] = useState("");
  const [votingMethod, setVotingMethod] = useState(VOTING_METHODS[0]);
  const [evidenceNote, setEvidenceNote] = useState("");
  const [employerName, setEmployerName] = useState("");
  const [filingForm, setFilingForm] = useState({ filing_submission_date: "", filing_office: "", filing_evidence_note: "" });
  const [accessDenied, setAccessDenied] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(() => {
    article36Api.get(agreementId)
      .then((r) => { setAgreement(r.data); setCoverage(r.data.covered_employee_ids || []); setAccessDenied(false); setNotFound(false); })
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); else if (err.response?.status === 404) setNotFound(true); });
    employeeApi.list().then((r) => setEmployees(r.data)).catch(() => {});
  }, [agreementId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (agreement?.election_id) {
      article36Api.getElection(agreement.election_id).then((r) => setElection(r.data));
    } else {
      setElection(null);
    }
  }, [agreement]);

  if (accessDenied) return <AccessDenied resourceLabel={t("article36.title")} />;
  if (notFound) return <NotFoundState testId="article36-not-found" backTo="/management/article36" />;
  if (!agreement) return <p className="text-sm text-gray-500">{t("common.loading")}</p>;

  const wrap = async (fn) => {
    try {
      await fn();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveCoverage = () => wrap(() => article36Api.update(agreement.id, { covered_employee_ids: coverage }));
  const openElection = () => wrap(async () => {
    await article36Api.openElection(agreement.id, { eligible_employee_ids: eligible, candidate_employee_id: candidate, voting_method: votingMethod });
    toast.success(t("article36.openElection"));
  });
  const closeElection = () => wrap(() => article36Api.closeElection(agreement.election_id));
  const certifyElection = () => wrap(() => article36Api.certifyElection(agreement.election_id, evidenceNote));
  const approve = () => wrap(() => article36Api.approve(agreement.id, employerName));
  const recordFiling = () => wrap(() => article36Api.recordFiling(agreement.id, filingForm));
  const activate = () => wrap(async () => { await article36Api.activate(agreement.id); toast.success(t("article36.activate")); });

  const toggleId = (list, setList, id) => setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  return (
    <div className="max-w-3xl space-y-6" data-testid="article36-detail-page">
      <PageHelpBanner pageKey="article36-detail" title="How to use this page">
        <p>Work through this workplace agreement top to bottom: confirm coverage, run the employee-representative election, get employer sign-off, record the labor office filing, then activate. Each panel only appears once the previous step is complete.</p>
      </PageHelpBanner>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900">{agreement.business_name}</h2>
          <p className="text-sm text-gray-500">{formatDate(agreement.effective_date)} → {formatDate(agreement.expiration_date)}</p>
        </div>
        <StatusBadge status={agreement.status} />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="article36-coverage-panel">
        <div className="flex items-center gap-2 mb-3"><Users size={16} className="text-blue-600" /><p className="text-sm font-semibold text-gray-800">{t("article36.coverage")}</p></div>
        <p className="text-xs text-gray-500 mb-3">{t("article36.coverageNote")}</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {employees.map((e) => (
            <label key={e.id} className="flex items-center gap-1.5 text-xs bg-gray-50 px-2.5 py-1.5 rounded-full border border-gray-200" data-testid={`article36-coverage-${e.id}`}>
              <input type="checkbox" checked={coverage.includes(e.id)} onChange={() => toggleId(coverage, setCoverage, e.id)} disabled={agreement.status !== "DRAFT"} />
              {e.legal_name}
            </label>
          ))}
        </div>
        {agreement.status === "DRAFT" && (
          <button data-testid="article36-save-coverage-button" onClick={saveCoverage} className="text-xs font-semibold text-blue-600 hover:underline">{t("common.save")}</button>
        )}
      </div>

      {agreement.status === "DRAFT" && !agreement.election_id && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="article36-open-election-panel">
          <div className="flex items-center gap-2 mb-3"><Vote size={16} className="text-blue-600" /><p className="text-sm font-semibold text-gray-800">{t("article36.openElection")}</p></div>
          <p className="text-xs text-gray-500 mb-2">{t("article36.eligibleVoters")}</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {employees.map((e) => (
              <label key={e.id} className="flex items-center gap-1.5 text-xs bg-gray-50 px-2.5 py-1.5 rounded-full border border-gray-200" data-testid={`article36-eligible-${e.id}`}>
                <input type="checkbox" checked={eligible.includes(e.id)} onChange={() => toggleId(eligible, setEligible, e.id)} />
                {e.legal_name}
              </label>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <select data-testid="article36-candidate-select" value={candidate} onChange={(e) => setCandidate(e.target.value)} className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm">
              <option value="">{t("article36.candidate")}</option>
              {employees.filter((e) => eligible.includes(e.id)).map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
            </select>
            <select data-testid="article36-voting-method-select" value={votingMethod} onChange={(e) => setVotingMethod(e.target.value)} className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm">
              {VOTING_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <button data-testid="article36-open-election-button" disabled={!candidate || eligible.length === 0} onClick={openElection} className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-40">{t("article36.openElection")}</button>
        </div>
      )}

      {election && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="article36-election-panel">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-800">{t("article36.electionStatus")}</p>
            <StatusBadge status={election.status} />
          </div>
          <div className="grid grid-cols-3 gap-3 text-center mb-4">
            <div data-testid="article36-votes-for"><p className="text-lg font-bold text-emerald-600">{election.votes_for}</p><p className="text-xs text-gray-500">{t("article36.votesFor")}</p></div>
            <div data-testid="article36-votes-against"><p className="text-lg font-bold text-red-500">{election.votes_against}</p><p className="text-xs text-gray-500">{t("article36.votesAgainst")}</p></div>
            <div data-testid="article36-abstentions"><p className="text-lg font-bold text-gray-500">{election.abstentions}</p><p className="text-xs text-gray-500">{t("article36.abstentions")}</p></div>
          </div>
          {election.status === "OPEN" && (
            <button data-testid="article36-close-election-button" onClick={closeElection} className="px-5 py-2 rounded-full bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 active:scale-95 transition-all">{t("article36.closeElection")}</button>
          )}
          {election.status === "CLOSED" && election.selection_result === "SELECTED" && (
            <div className="space-y-2">
              <input data-testid="article36-evidence-note-input" placeholder={t("article36.evidenceNote")} value={evidenceNote} onChange={(e) => setEvidenceNote(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
              <button data-testid="article36-certify-election-button" onClick={certifyElection} className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all">{t("article36.certifyElection")}</button>
            </div>
          )}
        </div>
      )}

      {agreement.status === "SIGNATURE_PENDING" && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="article36-approval-panel">
          <div className="flex items-center gap-2 mb-3"><PenLine size={16} className="text-blue-600" /><p className="text-sm font-semibold text-gray-800">{t("article36.approve")}</p></div>
          <input data-testid="article36-employer-name-input" placeholder={t("article36.employerRepName")} value={employerName} onChange={(e) => setEmployerName(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm mb-3" />
          <button data-testid="article36-approve-button" disabled={!employerName} onClick={approve} className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-40">{t("article36.approve")}</button>
        </div>
      )}

      {agreement.status === "READY_TO_FILE" && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="article36-filing-panel">
          <div className="flex items-center gap-2 mb-3"><FileCheck size={16} className="text-blue-600" /><p className="text-sm font-semibold text-gray-800">{t("article36.recordFiling")}</p></div>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <input data-testid="article36-filing-date-input" type="date" value={filingForm.filing_submission_date} onChange={(e) => setFilingForm((f) => ({ ...f, filing_submission_date: e.target.value }))} className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
            <input data-testid="article36-filing-office-input" placeholder={t("article36.filingOffice")} value={filingForm.filing_office} onChange={(e) => setFilingForm((f) => ({ ...f, filing_office: e.target.value }))} className="px-4 py-2.5 rounded-lg border border-gray-300 text-sm" />
          </div>
          <textarea data-testid="article36-filing-evidence-input" placeholder={t("article36.filingEvidence")} value={filingForm.filing_evidence_note} onChange={(e) => setFilingForm((f) => ({ ...f, filing_evidence_note: e.target.value }))} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm mb-3" rows={2} />
          <button data-testid="article36-record-filing-button" disabled={!filingForm.filing_submission_date || !filingForm.filing_office} onClick={recordFiling} className="px-5 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:scale-95 transition-all disabled:opacity-40">{t("article36.recordFiling")}</button>
        </div>
      )}

      {agreement.status === "FILED" && (
        <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="article36-activate-panel">
          <div className="flex items-center gap-2 mb-3"><Rocket size={16} className="text-blue-600" /><p className="text-sm font-semibold text-gray-800">{t("article36.activate")}</p></div>
          <button data-testid="article36-activate-button" onClick={activate} className="px-5 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 active:scale-95 transition-all">{t("article36.activate")}</button>
        </div>
      )}
    </div>
  );
}
